import React, { useRef, useEffect, useState, useCallback } from "react";

interface Tab {
  label: string;
  content: React.ReactNode;
}

interface LineTabsProps {
  tabs: Tab[];
  activeTab?: number;
  onTabChange: (index: number) => void;
  borderColor?: string;
  activeColor?: string;
  className?: string;
  position?: string;
}

const HeroTabs: React.FC<LineTabsProps> = ({
  tabs = [],
  activeTab = 0,
  onTabChange,
  borderColor = "border-gray-200",
  activeColor = "border-blue-500",
  className = "",
  position,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);

  const updateScrollState = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollWidth, clientWidth, scrollLeft } =
        scrollContainerRef.current;
      setShowScrollButtons(scrollWidth > clientWidth);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 2);
      setCanScrollLeft(scrollLeft > 2);
    }
  }, []);

  useEffect(() => {
    updateScrollState();
    window.addEventListener("resize", updateScrollState);
    return () => window.removeEventListener("resize", updateScrollState);
  }, [tabs, updateScrollState]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState);
    return () => el.removeEventListener("scroll", updateScrollState);
  }, [updateScrollState]);

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200;
      scrollContainerRef.current.scrollLeft +=
        direction === "left" ? -scrollAmount : scrollAmount;
    }
  };

  return (
    <div className={`${className}`}>
      <div className="relative">
        <div
          ref={scrollContainerRef}
          className={`
            flex overflow-x-auto scrollbar-hide
            ${position === "center" ? "justify-center" : ""}
            border-b ${borderColor}
          `}
        >
          <div className="flex gap-0  items-center min-w-min">
            {tabs.map((tab, index) => (
              <button
                key={index}
                onClick={() => onTabChange(index)}
                className={`
                  relative whitespace-nowrap px-2 md:px-4 h-[60.85px] -mb-px text-sm md:text-base font-medium 
                  transition-colors ease-in-out delay-200 w-fit md:w-[auto]
                  hover:text-bnp-cyan  bg-white first:rounded-tl-lg last:rounded-tr-lg
                  ${
                    activeTab === index
                      ? `text-bnp-cyan bg-opacity-10 font-bold border-b-4  first:rounded-bl-none last:rounded-br-none ${activeColor}`
                      : "text-white bg-opacity-[4%]  first:rounded-bl-lg last:rounded-br-lg "
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        {canScrollRight && (
          <button
            onClick={() => scroll("right")}
            className="absolute right-0 top-0 h-full w-8 flex items-center justify-center bg-bnp-dark rounded-tr-lg z-10"
            aria-label="Scroll right"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 3L11 8L6 13"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        {canScrollLeft && (
          <button
            onClick={() => scroll("left")}
            className="absolute left-0 top-0 h-full w-8 flex items-center justify-center bg-bnp-dark rounded-tl-lg z-10"
            aria-label="Scroll left"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 3L5 8L10 13"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
      <div className="mt-4">{tabs[activeTab]?.content}</div>
    </div>
  );
};

export default HeroTabs;
